import React, { useEffect, useState } from "react";

const DeviceAlertSettings = () => {
    const [devices, setDevices] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);

    // Load all devices (backend returns raw array)
    useEffect(() => {
        fetch("/api/devices")
            .then((res) => res.json())
            .then((data) => {
                console.log("Loaded devices:", data);
                setDevices(data || []);
            })
            .catch((err) => console.error("Device load error:", err));
    }, []);

    // Load alert settings when a device is selected
    useEffect(() => {
        if (!selectedId) {
            setSettings(null);
            return;
        }

        setLoading(true);
        fetch(`/api/device/${selectedId}/alert_settings`)
            .then((res) => res.json())
            .then((data) => {
                console.log("Loaded alert settings:", data);
                setSettings(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Settings load error:", err);
                setLoading(false);
            });
    }, [selectedId]);

    const updateField = (field, value) => {
        setSettings((prev) => ({ ...prev, [field]: value }));
    };

    const saveSettings = async () => {
        await fetch(`/api/device/${selectedId}/alert_settings`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(settings),
        });
        alert("Settings updated!");
    };
    const addQuietHour = () => {
        const empty = { start_utc: "", end_utc: "" };
        updateField("quiet_hours", [...(settings.quiet_hours || []), empty]);
    };

    const updateQuietHour = (index, field, value) => {
        const copy = [...(settings.quiet_hours || [])];
        copy[index][field] = value;
        updateField("quiet_hours", copy);
    };

    const removeQuietHour = (index) => {
        const copy = [...(settings.quiet_hours || [])];
        copy.splice(index, 1);
        updateField("quiet_hours", copy);
    };

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Device Alert Settings</h2>

            {/* Device selector */}
            <select
                className="border p-2 mb-4 rounded"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
            >
                <option value="">Select a device...</option>

                {devices.map((d) => (
                    <option key={d.id} value={d.id}>
                        {d.device_name}
                    </option>
                ))}
            </select>

            {loading && <div>Loading alert settings...</div>}

            {settings && !loading && (
                <div className="border rounded p-4 space-y-4 bg-gray-50">

                    {/* Alert Enabled */}
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={settings.alert_enabled}
                            onChange={(e) => updateField("alert_enabled", e.target.checked)}
                        />
                        <span className="font-semibold">Alert Enabled</span>
                    </label>

                    {/* Offline threshold */}
                    <div>
                        <label className="block font-semibold">
                            Offline Threshold (minutes)
                        </label>
                        <input
                            type="number"
                            className="border p-2 rounded w-32"
                            value={settings.offline_threshold_minutes}
                            onChange={(e) =>
                                updateField("offline_threshold_minutes", Number(e.target.value))
                            }
                        />
                    </div>

                    {/* Notify interval */}
                    <div>
                        <label className="block font-semibold">
                            Notify Interval (minutes)
                        </label>
                        <input
                            type="number"
                            className="border p-2 rounded w-32"
                            value={settings.notify_interval_minutes}
                            onChange={(e) =>
                                updateField("notify_interval_minutes", Number(e.target.value))
                            }
                        />
                    </div>

                    {/* Run delay */}
                    <div>
                        <label className="block font-semibold">
                            Run Delay (minutes)
                        </label>
                        <input
                            type="number"
                            className="border p-2 rounded w-32"
                            value={settings.run_delay_minutes}
                            onChange={(e) =>
                                updateField("run_delay_minutes", Number(e.target.value))
                            }
                        />
                    </div>
                    {/* Notify Emails */}
                    <div>
                        <label className="block font-semibold">Notify Emails (separated by ;)</label>
                        <textarea
                            className="border p-2 rounded w-full"
                            rows={2}
                            value={settings.notify_emails || ""}
                            onChange={(e) => updateField("notify_emails", e.target.value)}
                        />
                    </div>
                    {/* Quiet Hours */}
                    <div className="border rounded p-3 bg-white">
                        <div className="flex justify-between items-center mb-2">
                            <label className="font-semibold text-lg">Quiet Hours (UTC)</label>
                            <button
                                onClick={addQuietHour}
                                className="px-2 py-1 bg-green-600 text-white text-sm rounded"
                            >
                                + Add Range
                            </button>
                        </div>

                        {(settings.quiet_hours || []).length === 0 && (
                            <div className="text-gray-500 text-sm">No quiet hours added.</div>
                        )}

                        {(settings.quiet_hours || []).map((qh, idx) => (
                            <div key={idx} className="p-3 mb-3 border rounded bg-gray-100 space-y-2">

                                <div className="flex gap-4">
                                    {/* Start */}
                                    <div>
                                        <label className="block font-semibold text-sm">Start (UTC)</label>
                                        <input
                                            type="time"
                                            className="border p-2 rounded"
                                            value={qh.start_utc}
                                            onChange={(e) =>
                                                updateQuietHour(idx, "start_utc", e.target.value)
                                            }
                                        />
                                    </div>

                                    {/* End */}
                                    <div>
                                        <label className="block font-semibold text-sm">End (UTC)</label>
                                        <input
                                            type="time"
                                            className="border p-2 rounded"
                                            value={qh.end_utc}
                                            onChange={(e) =>
                                                updateQuietHour(idx, "end_utc", e.target.value)
                                            }
                                        />
                                    </div>
                                </div>

                                <button
                                    className="px-2 py-1 bg-red-600 text-white text-sm rounded"
                                    onClick={() => removeQuietHour(idx)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={saveSettings}
                        className="px-4 py-2 bg-blue-600 text-white rounded shadow"
                    >
                        Save
                    </button>
                </div>
            )}
        </div>
    );
};

export default DeviceAlertSettings;
